// Pure panel-placement math for the desktop floating bug-report pill.
//
// Extracted from `BugReportButton.tsx` so the component stays under the
// file-size hygiene soft cap, and so the placement logic can be unit-
// tested in isolation. Logic: prefer rendering the panel ABOVE the pill
// (matches the legacy bottom-4 muscle memory). If there's < 240px of
// vertical room above, OR more room below than above, flip the panel
// BELOW the pill instead. Horizontal position anchored to the pill's
// left edge but clamped to the viewport with a small margin.
//
// The pill itself is hidden while the panel is open (see
// `BugReportButton`); this placement still anchors near the pill's
// last position so the panel feels connected to where the user clicked.

export type PanelPlacementArgs = {
  /** Pill top-left in viewport coords. */
  pillX: number;
  pillY: number;
  /** Pill measured size. */
  pillW: number;
  pillH: number;
  /** Current viewport size. */
  viewportW: number;
  viewportH: number;
  /** Worst-case panel height; the panel itself caps to 80vh so real height may be smaller. */
  panelMaxHeight: number;
  /** Panel render width (constant; 320). */
  panelWidth: number;
  /** Gap between the panel and the pill anchor. */
  offsetPx: number;
  /** Padding from viewport edges. */
  viewportMarginPx: number;
};

export type PanelPlacement = {
  left: number;
  top: number;
  /** Which side of the pill the panel flipped to. Useful for tests / future styling. */
  placement: "above" | "below";
};

/** Below this many px of room above the pill, the panel flips to render below. */
const MIN_ABOVE_ROOM_PX = 240;

export function computePanelPlacement(args: PanelPlacementArgs): PanelPlacement {
  const {
    pillX,
    pillY,
    pillH,
    viewportW,
    viewportH,
    panelMaxHeight,
    panelWidth,
    offsetPx,
    viewportMarginPx,
  } = args;

  const roomAbove = pillY - offsetPx - viewportMarginPx;
  const roomBelow = viewportH - (pillY + pillH) - offsetPx - viewportMarginPx;
  const preferAbove = roomAbove >= MIN_ABOVE_ROOM_PX || roomAbove >= roomBelow;

  const top = preferAbove
    ? Math.max(
        viewportMarginPx,
        pillY - offsetPx - Math.min(panelMaxHeight, Math.max(0, roomAbove)),
      )
    : pillY + pillH + offsetPx;

  const left = Math.max(
    viewportMarginPx,
    Math.min(pillX, viewportW - panelWidth - viewportMarginPx),
  );

  return { left, top, placement: preferAbove ? "above" : "below" };
}
